import { useFindAllOrdersQuery } from '../../api/api-hooks';
import {
  DataGrid,
  GridCellParams,
  GridCellValue,
  GridColDef,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import { truncateISOString } from '../../utils/dateTime';
import { CircularProgress, Grid } from '@mui/material';
import React from 'react';
import { Item } from '../../utils/sharedTypes';
import SupportPurchasesButton from '../support-purchases/SupportPurchasesButton';
import { OrderItemFilter } from './ordersTypes';
import { Order } from '../../api/api-generated';
import { filterOrder } from './ordersUtils';
import HardwareButton from '../hardware/HardwareButton';
import LicensesButton from '../licenses/LicensesButton';
import LptRmaButton from '../lpt-rma/LptRmaButton';
import DownloadButton from '../../components/DownloadButton';
import { useGridSort } from '../../hooks/useGridSort';
import { CompleteOrderButton } from './CompleteOrderButton';

interface Props {
  setSelectedOrders: React.Dispatch<React.SetStateAction<Item[]>>;
  filters: OrderItemFilter;
  includeCompleted: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const OrdersTable = ({
  setSelectedOrders,
  filters,
  includeCompleted,
  setEditModalOpenFlag,
}: Props) => {
  const { data: orders } = useFindAllOrdersQuery({});
  const { sortModel, handleSortModelChange } = useGridSort('id', 'desc');
  if (orders === undefined) return <CircularProgress></CircularProgress>;
  const columns: GridColDef[] = [
    { field: 'id', hide: true },
    { field: 'purchaseOrderLink', hide: true },
    { field: 'quoteLink', hide: true },
    { field: 'companyName', headerName: 'Company', width: 150 },
    {
      field: 'purchaseOrderName',
      headerName: 'Purchase Order',
      width: 200,
    },
    {
      field: 'purchaseOrderDownload',
      headerName: ' ',
      width: 70,
      renderCell: (params: GridCellParams) => {
        if (params.row.purchaseOrderName && params.row.purchaseOrderLink)
          return (
            <DownloadButton
              label={params.row.purchaseOrderName}
              url={`orders/purchase-order/${params.row.id}`}
            />
          );
      },
    },
    {
      field: 'quoteName',
      headerName: 'Quote',
      width: 200,
    },
    {
      field: 'quoteDownload',
      headerName: ' ',
      width: 70,
      renderCell: (params: GridCellParams) => {
        if (params.row.quoteName && params.row.quoteLink)
          return (
            <DownloadButton
              label={params.row.quoteName}
              url={`orders/quote/${params.row.id}`}
            />
          );
      },
    },
    { field: 'orderDate', headerName: 'Date', width: 150 },
    { field: 'price', headerName: 'Price', width: 150 },
    {
      field: 'buttons',
      headerName: ' ',
      width: 550,
      renderCell: (params) => {
        const ordersId: GridCellValue = params.getValue(params.id, 'id');
        const clickedOrder = orders.find((order) => order.id === ordersId);
        if (!clickedOrder) return '';
        return (
          <Grid container spacing={1}>
            <Grid item>
              <SupportPurchasesButton order={clickedOrder} />
            </Grid>
            <Grid item>
              <HardwareButton order={clickedOrder} />
            </Grid>
            <Grid item>
              <LicensesButton order={clickedOrder} />
            </Grid>
            <Grid item>
              <LptRmaButton order={clickedOrder} />
            </Grid>
            {!clickedOrder.completed && (
              <Grid item>
                <CompleteOrderButton order={clickedOrder} />
              </Grid>
            )}
          </Grid>
        );
      },
    },
  ];
  const rows = orders
    .filter((item: Order) =>
      includeCompleted
        ? item.completed && filterOrder(item, filters)
        : !item.completed && filterOrder(item, filters)
    )
    .map((item: Order) => {
      return {
        ...item,
        companyName: item.company.companyName,
        orderDate:
          item.orderDate === null ? 'NA' : truncateISOString(item.orderDate),
        completed: !!item.completed,
      };
    });
  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedOrders(orders.filter((order) => model.includes(order.id)));
  };
  const handleRowClick = (params: GridRowParams) => {
    setSelectedOrders(
      orders.filter((order: Order) => order.id === params.row.id)
    );
    setEditModalOpenFlag(true);
  };
  return (
    <div style={{ paddingBottom: '1rem', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={100}
        checkboxSelection
        disableSelectionOnClick
        autoHeight
        disableColumnMenu={true}
        onSelectionModelChange={handleTableSelection}
        onRowClick={handleRowClick}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />
    </div>
  );
};

export default OrdersTable;
